import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { take } from 'rxjs/operators';
import { MembershipService } from '../services/membership.service';
import { PreferencesService } from '../services/preferences.service';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [TranslateModule],
  template: `
    <div class="auth-callback">
      <div class="auth-dots">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
      <span class="auth-callback-text">{{ 'auth.signingIn' | translate }}</span>
    </div>
  `,
  styles: [`
    .auth-callback {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      height: 60vh;
      gap: 1.25rem;
    }
    .auth-dots {
      display: flex;
      gap: 6px;
    }
    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: var(--p-text-muted-color);
      animation: dot-wave 1.4s ease-in-out infinite;
    }
    .dot:nth-child(2) { animation-delay: 0.16s; }
    .dot:nth-child(3) { animation-delay: 0.32s; }
    @keyframes dot-wave {
      0%, 60%, 100% { transform: translateY(0); opacity: 0.3; }
      30% { transform: translateY(-8px); opacity: 1; }
    }
    .auth-callback-text {
      color: var(--p-text-muted-color);
      font-size: 0.875rem;
      font-weight: 300;
      letter-spacing: 0.02em;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuthCallbackComponent implements OnInit {
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly router = inject(Router);
  private readonly membershipService = inject(MembershipService);
  private readonly preferencesService = inject(PreferencesService);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);

  ngOnInit() {
    // A failed silent SSO probe (prompt=none) redirects back here with ?error=login_required when the auth
    // server has no active session — i.e. the visitor is confirmed anonymous. There is no code to exchange,
    // so don't run checkAuth; enter the anonymous terminal state instead. This is the SAME resolution
    // app.component.resolveUnauthenticatedRoute() performs on hint-absent entries, but it can't run here:
    // app.component.ngOnInit early-returns on the /auth/signin-oidc load, and the SPA navigation back
    // doesn't re-invoke it. loadAnonymousPreferences() applies the content language for this terminal
    // state — without it content paints in the boot guess (the per-brand resolution was added 2026-06-17;
    // this path was never wired to it). Strip the error query so a refresh doesn't re-process it. The probe now fires on
    // EVERY route, public pages included — so a declined probe restores the STORED return route (a share
    // link opened with a stale hint must land on the shared collection, not the portal root).
    if (new URLSearchParams(window.location.search).get('error')) {
      this.preferencesService.loadAnonymousPreferences();
      // Strip the query but keep the entry's state: the router's refused-navigation restoration
      // reads its own page id from it.
      window.history.replaceState(window.history.state, '', window.location.pathname);
      this.navigateToStoredOrRoot();
      return;
    }

    // The round trip's identity: the state the sign-in call minted, brought back on this URL.
    // Read BEFORE the exchange — it validates the state and then strips the callback query.
    const returnedState = new URLSearchParams(window.location.search).get('state');

    // The OIDC library processes the callback automatically when checkAuth is called.
    // We need to wait for it to complete before deciding where to navigate.
    this.oidcSecurityService.checkAuth().pipe(take(1)).subscribe({
      next: ({ isAuthenticated }) => {
        if (!isAuthenticated) {
          // No session established (e.g. expired/invalid tokens). Confirmed anonymous → apply the anonymous content language.
          this.recoverAsAnonymous();
          return;
        }

        // Successful auth consumes the probe-once marker: the next cold entry with the
        // session hint may probe again.
        sessionStorage.removeItem('sso_checked');

        // Delegate post-auth bootstrap to MembershipService: it loads brand settings
        // + current user (so membership-aware UI becomes accurate immediately after
        // sign-in) and, if THIS round trip is the one an intentional Open-brand signup
        // started (its note names the hop; the returned state proves it), also performs
        // the auto-join. Returns true only in the auto-join case — then the admission
        // itself re-boots the document from the membership service's one state door once
        // the join's re-read lands the active standing (nothing to navigate here);
        // otherwise fall back to the stored URL.
        this.membershipService.initializeAfterAuth(returnedState).subscribe(outcome => {
          if (outcome === 'joined') return;
          // The sign-up's auto-join met a full brand: the join became a pending request, the admins
          // have been told — the person reads that and lands where they were going, never on the
          // spinner waiting for an admission that is not coming.
          if (outcome === 'awaiting') {
            this.messageService.add({
              severity: 'info',
              summary: this.translate.instant('requestAccess.brandFull', { brand: this.membershipService.brandInfo()?.name ?? '' }),
              life: 6000,
            });
          }
          this.navigateToStoredOrRoot();
        });
      },
      error: (err) => {
        // Stale callback URL — state was already consumed (autoCleanStateAfterAuthentication
        // clears authStateControl after a successful exchange). Reaching this branch means
        // the browser reloaded at /auth/signin-oidc?state=X&code=Y after the original
        // exchange completed. Strip query params and send the user to the portal.
        console.warn('[Auth] OIDC callback re-run detected, recovering:', err);
        window.history.replaceState(window.history.state, '', window.location.pathname);
        this.router.navigate(['/'], { replaceUrl: true });
      }
    });
  }

  /**
   * Confirmed-anonymous terminal state: resolve anonymous preferences (which applies the brand's
   * default content language, so content paints in the right lens) and show the public portal.
   * Mirrors app.component.resolveUnauthenticatedRoute()'s anonymous branch.
   */
  private recoverAsAnonymous(): void {
    this.preferencesService.loadAnonymousPreferences();
    this.router.navigate(['/'], { replaceUrl: true });
  }

  /**
   * Every exit REPLACES the callback's history entry: the callback is an interstitial, never a
   * Back target (the notifications-landing grammar). Leaving it in history would also leave an
   * entry whose state the OIDC library's own history cleanup has already wiped, so a refused leave
   * landing there would have nothing to restore and the address bar and view would desync
   * (ruled 2026-09-01). Back from the first page after sign-in now goes to whatever preceded the
   * sign-in.
   */
  private navigateToStoredOrRoot(): void {
    const storedUrl = sessionStorage.getItem('authRedirectUrl');
    if (storedUrl) {
      sessionStorage.removeItem('authRedirectUrl');
      this.router.navigateByUrl(storedUrl, { replaceUrl: true });
    } else {
      this.router.navigate(['/'], { replaceUrl: true });
    }
  }
}
